'use client';

import { ArrowLeft, Facebook, Globe, Instagram, Linkedin, Twitter } from 'lucide-react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import React from 'react';

import pfpFallback from '@ui/assets/images/pfp-fallback.png';
import StarFilled from '@ui/components/icons/StarFilled';

import type { ProfessionalProfileProps } from './DesktopProfessionalProfile';
import { ProfessionalShareButton } from './ProfessionalShareButton';

export function MobileProfessionalProfile({
  id,
  name,
  profileImageUrl,
  label,
  joinedYear,
  bookingsCount,
  ratingAverage,
  bio,
  socials,
  activitiesContent,
  reviewsContent,
  showReviews = true,
  translations: t,
  className = ''
}: ProfessionalProfileProps) {
  const router = useRouter();
  const displaySocials = socials;
  const hasSocialLinks = displaySocials && Object.values(displaySocials).some((value) => !!value);

  return (
    <section className={`w-full pb-8 relative ${className}`}>
      {/* Back button — top-left of the profile section (scrolls with content) */}
      <div className="absolute top-4 left-4 z-10">
        <button
          type="button"
          onClick={() => router.back()}
          aria-label="Go back"
          className="flex items-center justify-center size-10 rounded-full bg-white text-neutral-900 shadow-md cursor-pointer hover:bg-neutral-50 hover:shadow-lg transition-all"
        >
          <ArrowLeft className="size-5" />
        </button>
      </div>

      {/* Share button — top-right of the profile section (scrolls with content) */}
      <div className="absolute top-4 right-4 z-10">
        <ProfessionalShareButton professionalId={id} professionalName={name} iconOnly />
      </div>

      {/* Profile header */}
      <div className="flex flex-col items-center text-center gap-2 px-6 pt-20 pb-6">
        <div className="w-28 h-28 relative rounded-full overflow-hidden border border-gray-200 mb-2">
          {profileImageUrl ? (
            <Image src={profileImageUrl} alt={name} fill className="object-cover" priority sizes="112px" />
          ) : (
            <Image src={pfpFallback} alt={name} fill className="object-cover" sizes="112px" />
          )}
        </div>

        <h1 className="text-xl font-bold text-neutral-900">{name}</h1>
        <p className="text-neutral-500 text-sm">{label}</p>

        <div className="flex justify-center items-center gap-x-2 mt-1 text-xs text-neutral-600">
          <span>
            {t.instructorSince} {joinedYear}
          </span>
          <span className="text-neutral-300">•</span>
          <span>
            {bookingsCount} {t.bookings}
          </span>
          {ratingAverage != null && ratingAverage > 0 && (
            <>
              <span className="text-neutral-300">•</span>
              <span className="flex items-center gap-1">
                {ratingAverage.toFixed(1)}
                <StarFilled className="w-3 h-3 text-[#FDC812]" />
              </span>
            </>
          )}
        </div>
      </div>

      {/* Bio */}
      <div className="px-5 py-5 border-t border-neutral-100">
        {bio ? (
          <p className="text-sm text-neutral-800 leading-relaxed whitespace-pre-line">{bio}</p>
        ) : (
          <p className="text-sm text-neutral-400 italic">{t.noBioYet}</p>
        )}
      </div>

      {/* Social media links */}
      {hasSocialLinks && (
        <div className="px-5 py-5 border-t border-neutral-100">
          <h2 className="text-base font-semibold text-neutral-900 mb-3">{t.socialMedia}</h2>
          <div className="flex gap-4">
            {displaySocials.website && (
              <a
                href={
                  displaySocials.website.startsWith('http')
                    ? displaySocials.website
                    : `https://${displaySocials.website}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-400 hover:text-primary-500 transition-colors"
                aria-label="Website"
              >
                <Globe size={20} />
              </a>
            )}
            {displaySocials.instagram && (
              <a
                href={`https://instagram.com/${displaySocials.instagram.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-400 hover:text-primary-500 transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
            )}
            {displaySocials.facebook && (
              <a
                href={`https://facebook.com/${displaySocials.facebook}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-400 hover:text-primary-500 transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
            )}
            {displaySocials.twitter && (
              <a
                href={`https://twitter.com/${displaySocials.twitter.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-400 hover:text-primary-500 transition-colors"
                aria-label="Twitter"
              >
                <Twitter size={20} />
              </a>
            )}
            {displaySocials.linkedin && (
              <a
                href={`https://linkedin.com/in/${displaySocials.linkedin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-400 hover:text-primary-500 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
            )}
          </div>
        </div>
      )}

      {/* Activities */}
      <div className="px-5 py-5 border-t border-neutral-100">
        <h2 className="text-base font-semibold text-neutral-900 mb-4">{t.activities}</h2>
        {activitiesContent ? (
          activitiesContent
        ) : (
          <p className="text-sm text-neutral-400 italic text-center py-4">{t.noActivitiesFound}</p>
        )}
      </div>

      {/* Reviews */}
      {showReviews && (
        <div className="px-5 py-5 border-t border-neutral-100">
          <h2 className="text-base font-semibold text-neutral-900 mb-4">{t.reviews}</h2>
          {reviewsContent ? (
            reviewsContent
          ) : (
            <p className="text-sm text-neutral-400 italic text-center py-4">{t.noReviewsYet}</p>
          )}
        </div>
      )}
    </section>
  );
}
